import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Helmet } from "react-helmet-async";
import { useTranslation } from 'react-i18next';
import { FaTruck, FaEnvelope } from 'react-icons/fa';

import products_fr from '../../data/products_fr';
import products_en from '../../data/products_en';
import QuantitySelector from '../../components/Buttons/QuantitySelector';
import RightSidebar from '../../components/Side/SideBarPanier';
import { addToCart } from '../../redux/cartSlice';

/* Accordion facon DA (comme la FAQ), avec icone. */
const AccordionRow = ({ icon: Icon, title, content, isOpen, onClick }) => (
  <div
    className={`rounded-2xl border transition-colors duration-300 ${isOpen ? 'bg-white shadow-md' : 'bg-white/60 hover:bg-white'}`}
    style={{ borderColor: isOpen ? 'rgba(217,166,65,0.5)' : 'rgba(120,90,40,0.15)' }}
  >
    <button
      onClick={onClick}
      className="w-full text-left px-5 py-4 flex items-center justify-between gap-4"
      aria-expanded={isOpen}
    >
      <span className="flex items-center gap-3 font-sans text-base md:text-lg text-stone-800">
        <Icon style={{ color: '#C8951F' }} />
        {title}
      </span>
      <span
        className="flex-shrink-0 transition-transform duration-300"
        style={{ transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)', color: '#D9A641' }}
        aria-hidden="true"
      >
        <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
          <path d="M5 7.5 L10 12.5 L15 7.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </span>
    </button>
    <div className={`grid transition-all duration-300 ease-out ${isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`}>
      <div className="overflow-hidden">
        <p className="px-5 pb-5 font-nunito text-stone-600 leading-relaxed">{content}</p>
      </div>
    </div>
  </div>
);

const ProductDetail = () => {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const products = i18n.language === 'fr' ? products_fr : products_en;
  const product = products.find((p) => p.id === id);

  const dispatch = useDispatch();
  const [expandedSections, setExpandedSections] = useState({ delivery: false, contact: false });
  const [isCartSidebarOpen, setIsCartSidebarOpen] = useState(false);
  const [selectedQuantity, setSelectedQuantity] = useState(1);

  const toggleSection = (section) =>
    setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }));

  const toggleCartSidebar = () => setIsCartSidebarOpen((v) => !v);

  const addToCartt = () => {
    if (!product || product.stock === 0) return;
    dispatch(addToCart({
      id: product.id, title: product.title, image: product.imageLg, price: product.price, quantity: selectedQuantity,
    }));
    toggleCartSidebar();
  };

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#FBF7EF' }}>
        <p className="font-nunito text-stone-600">Product not found</p>
      </div>
    );
  }

  const outOfStock = product.stock === 0;
  const stockWidth = `${Math.min(Number(product.stock) || 0, 100)}%`;
  const stockColor = product.stock > 50 ? '#4F7A3A' : '#B4452E';
  const teaser = Array.isArray(product.description) ? product.description : [product.description];

  return (
    <div style={{ backgroundColor: '#FBF7EF' }}>
      <Helmet>
        <title>{`${product.title} | Au Coing du Ventoux`}</title>
        <meta name="description" content={`Achetez ${product.title}, un produit artisanal. Livraison rapide.`} />
        <meta property="og:title" content={product.title} />
        <meta property="og:description" content={`Achetez ${product.title}, un produit artisanal. Livraison rapide.`} />
        <meta property="og:image" content={product.imageLg} />
        <meta property="og:type" content="product" />
        <meta property="og:url" content={`https://aucoingduventoux.com/product/${id}`} />
        <link rel="canonical" href={`https://aucoingduventoux.com/product/${id}`} />
      </Helmet>

      <div className="max-w-6xl mx-auto px-6 md:px-10 pt-40 md:pt-52 pb-24">
        {/* Retour boutique */}
        <Link to="/boutique" className="inline-flex items-center gap-2 font-sans text-sm text-stone-500 hover:text-stone-800 transition-colors mb-8">
          <span aria-hidden="true">&larr;</span> Boutique
        </Link>

        <div className="flex flex-col md:flex-row gap-10 lg:gap-16 items-start">
          {/* Image (panneau encadre, colle au scroll sur desktop) */}
          <div className="w-full md:w-1/2 md:sticky md:top-32">
            <div
              className="rounded-3xl overflow-hidden flex items-center justify-center p-8 shadow-lg"
              style={{ background: 'linear-gradient(160deg, #EFE2CB 0%, #E4D2B2 100%)', minHeight: '420px' }}
            >
              <img
                src={product.imageLg}
                alt={product.title}
                className="max-h-[340px] md:max-h-[560px] w-auto object-contain drop-shadow-xl"
              />
            </div>
          </div>

          {/* Infos */}
          <div className="w-full md:w-1/2">
            <span className="text-xs font-semibold tracking-[0.3em] uppercase text-amber-800">
              Au Coing du Ventoux
            </span>
            <h1 className="font-sans text-3xl md:text-4xl text-stone-900 mt-3 leading-tight">{product.title}</h1>

            <p className="font-sans text-3xl mt-4" style={{ color: '#A9760F' }}>
              {product.price.toFixed(2)}&nbsp;€
            </p>

            <hr className="my-7" style={{ borderColor: 'rgba(120,90,40,0.15)' }} />

            {/* Stock */}
            <div>
              <p className="font-sans text-sm tracking-wide text-stone-700 mb-2">
                {t('product.details.stock')} — {product.quantitytitle}
              </p>
              <div className="w-full rounded-full h-2 overflow-hidden" style={{ backgroundColor: 'rgba(120,90,40,0.15)' }}>
                <div className="h-2 rounded-full" style={{ width: stockWidth, backgroundColor: stockColor }} />
              </div>
            </div>

            {/* Quantite */}
            <div className="mt-7">
              <p className="font-sans text-sm tracking-wide text-stone-700 mb-2">{t('product.details.quantity')}</p>
              <div className="inline-flex">
                <QuantitySelector onQuantityChange={setSelectedQuantity} initialQuantity={1} />
              </div>
            </div>

            {outOfStock && (
              <p className="text-red-700 mt-3 text-sm font-semibold">
                {t('product.details.out_of_stock_message') || "Ce produit est momentanément indisponible."}
              </p>
            )}

            {/* Ajouter au panier */}
            <button
              onClick={addToCartt}
              disabled={outOfStock}
              className={`w-full mt-6 font-sans tracking-wide rounded-xl py-4 text-white transition-all duration-300 ${
                outOfStock ? 'cursor-not-allowed' : 'hover:opacity-90 hover:scale-[1.01]'
              }`}
              style={{ backgroundColor: outOfStock ? '#9C9389' : '#2E2318' }}
            >
              {outOfStock
                ? (t('product.details.out_of_stock') || 'Rupture de stock')
                : t('product.details.add_to_cart')}
            </button>

            {/* Description */}
            <div className="mt-10">
              <h2 className="font-sans text-xl md:text-2xl text-stone-900 mb-3">
                {t('product.details.description')}
              </h2>
              <div className="space-y-3">
                {teaser.map((line, index) => (
                  <p key={index} className="font-nunito text-stone-600 leading-relaxed">{line}</p>
                ))}
              </div>
            </div>

            {/* Accordeons */}
            <div className="mt-8 space-y-3">
              <AccordionRow
                icon={FaTruck}
                title={t('product.details.delivery.title')}
                content={t('product.details.delivery.content')}
                isOpen={expandedSections.delivery}
                onClick={() => toggleSection('delivery')}
              />
              <AccordionRow
                icon={FaEnvelope}
                title={t('product.details.contact.title')}
                content={t('product.details.contact.content')}
                isOpen={expandedSections.contact}
                onClick={() => toggleSection('contact')}
              />
            </div>
          </div>
        </div>

        <RightSidebar isOpen={isCartSidebarOpen} toggleSidebar={toggleCartSidebar} />
      </div>
    </div>
  );
};

export default ProductDetail;